import React, { useEffect, useMemo, useState } from 'react';
import api, { BASE as API_BASE } from '../services/api';
import { toast } from '../utils/toastBus';
import { Button, Select, Table, EmptyState } from '../components/ui';

export default function MyUploads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('createdAt:desc');
  const [search, setSearch] = useState('');
  const [ttlMs, setTtlMs] = useState(5 * 60 * 1000);
  const [transcribing, setTranscribing] = useState({}); // { [name]: true }
  const [transcripts, setTranscripts] = useState({}); // { [name]: { text, id } }

  function formatBytes(bytes) {
    const n = typeof bytes === 'number' ? bytes : Number(bytes);
    if (!n || n < 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(n) / Math.log(1024));
    const v = (n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1);
    return `${v} ${units[i]}`;
  }

  async function onTranscribe(file) {
    const name = file?.name;
    if (!name) return;
    const href = resolveMediaUrl(file.url);
    try {
      setTranscribing((m) => ({ ...m, [name]: true }));
      const res = await api.transcribe({ sourceUrl: href });
      if (res?.error) throw new Error(res.error);
      const text = res?.item?.text || '';
      const id = res?.id ?? res?.item?.id ?? null;
      setTranscripts((m) => ({ ...m, [name]: { text, id } }));
      if (!text) toast({ type: 'warn', message: 'Transcribed with empty result' });
      else toast({ type: 'success', message: 'Transcription ready' });
    } catch (e) {
      toast({ type: 'error', message: e?.message || 'Transcription failed' });
    } finally {
      setTranscribing((m) => ({ ...m, [name]: false }));
    }
  }

  function resolveMediaUrl(u) {
    if (!u || typeof u !== 'string') return '';
    if (u.startsWith('/uploads/')) return `${API_BASE}${u}`;
    return u;
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listMyUploads({ page, limit: pageSize, sort });
      if (res?.error) throw new Error(res.error);
      const arr = Array.isArray(res?.items) ? res.items : [];
      setItems(arr);
      setTotal(typeof res?.total === 'number' ? res.total : arr.length);
    } catch (e) {
      setError(e.message || 'Failed to load uploads');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [page, pageSize, sort]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it => String(it.name || '').toLowerCase().includes(q));
  }, [items, search]);
  const maxPage = Math.max(1, Math.ceil((filtered.length || total) / pageSize));
  const safePage = Math.min(page, maxPage);
  const start = (safePage - 1) * pageSize;
  const end = start + pageSize;
  const pageItems = filtered.slice(start, end);

  async function onDelete(name) {
    try {
      const ok = window.confirm(`Delete ${name}? This cannot be undone.`);
      if (!ok) return;
      const res = await api.deleteMyUpload(name);
      if (res?.ok === true) {
        toast({ type: 'success', message: 'Deleted upload' });
        // reload current page; if empty and not first page, go back one
        await load();
        if (items.length === 1 && page > 1) setPage(p => Math.max(1, p - 1));
      } else {
        throw new Error(res?.error || 'Delete failed');
      }
    } catch (e) {
      toast({ type: 'error', message: e?.message || 'Delete failed' });
    }
  }

  return (
    <div className="container" style={{ padding: 16 }}>
      <h2 style={{ color: '#ff7a45' }}>My Uploads</h2>
      <p className="muted">Files you have uploaded. Links are served from the backend origin. Generate expiring links for sharing.</p>
      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <p style={{ color: '#fca5a5' }}>{error}</p>
      ) : (
        <>
        <div className="row" style={{ margin: '4px 0 8px', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="search"
            placeholder="Search by filename…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ minWidth: 220, padding: '6px 8px', background: '#0b0b0e', border: '1px solid #1f2937', color: '#e5e7eb', borderRadius: 6 }}
            aria-label="Search uploads"
          />
          <Select id="myuploads-page-size" name="pageSize" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </Select>
          <Select id="myuploads-sort" name="sort" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
            <option value="createdAt:desc">Newest</option>
            <option value="createdAt:asc">Oldest</option>
            <option value="name:asc">Name A–Z</option>
            <option value="name:desc">Name Z–A</option>
            <option value="size:desc">Size High→Low</option>
            <option value="size:asc">Size Low→High</option>
          </Select>
          <label htmlFor="ttl" style={{ color: '#9ca3af', marginLeft: 8 }}>Link TTL:</label>
          <Select id="ttl" value={ttlMs} onChange={(e) => setTtlMs(Number(e.target.value))}>
            <option value={5 * 60 * 1000}>5 minutes</option>
            <option value={60 * 60 * 1000}>1 hour</option>
            <option value={24 * 60 * 60 * 1000}>24 hours</option>
          </Select>
        </div>
        <Table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Size</th>
              <th>Modified</th>
              <th>Preview</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((f) => {
              const href = resolveMediaUrl(f.url);
              const isAudio = /\.(mp3|wav|m4a|webm|ogg|aac|flac|mp4|m4b)$/i.test(f.name || '');
              return (
                <>
                  <tr key={f.name}>
                    <td style={{ padding: '4px 4px', borderBottom: '1px solid #111827', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</td>
                    <td style={{ padding: '4px 4px', borderBottom: '1px solid #111827' }}>{formatBytes(f.size)}</td>
                    <td style={{ padding: '4px 4px', borderBottom: '1px solid #111827' }}>{f.modified}</td>
                    <td style={{ padding: '4px 4px', borderBottom: '1px solid #111827' }}>
                      {isAudio && href ? (
                        <audio controls src={href} style={{ height: 24, maxWidth: 200 }} />
                      ) : (
                        <span style={{ color: '#6b7280' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '4px 4px', borderBottom: '1px solid #111827', whiteSpace: 'nowrap' }}>
                      <a href={href} target="_blank" rel="noreferrer" style={{ color: '#60a5fa', marginRight: 8 }}>Open</a>
                      <a href={href} download style={{ color: '#34d399', marginRight: 8 }}>Download</a>
                      <Button onClick={async () => {
                        try {
                          if (String(f.url || '').startsWith('/uploads/')) {
                            const { url, expires } = await api.signDownload(String(f.url), ttlMs);
                            if (url) {
                              await navigator.clipboard.writeText(url);
                              const mins = expires ? Math.max(1, Math.round((Number(expires) - Date.now())/60000)) : null;
                              toast({ type: 'success', message: `Copied signed link${mins ? ` (expires in ${mins}m)` : ''}` });
                            } else {
                              throw new Error('Failed to sign');
                            }
                          } else {
                            await navigator.clipboard.writeText(href);
                            toast({ type: 'success', message: 'Copied link' });
                          }
                        } catch (e) {
                          toast({ type: 'error', message: e?.message || 'Copy failed' });
                        }
                      }} style={{ marginRight: 8 }}>Copy Link</Button>
                      <Button onClick={() => onDelete(f.name)} variant="danger">Delete</Button>
                      <Button onClick={() => onTranscribe(f)} disabled={!!transcribing[f.name]} style={{ marginLeft: 8 }}>
                        {transcribing[f.name] ? 'Transcribing…' : 'Transcribe'}
                      </Button>
                    </td>
                  </tr>
                  {transcripts[f.name]?.text ? (
                    <tr key={`${f.name}__tx`}>
                      <td colSpan={5} style={{ padding: '6px 6px', background: '#0b0b0e', borderBottom: '1px solid #111827' }}>
                        <div style={{ color: '#e5e7eb', maxHeight: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>{transcripts[f.name].text}</div>
                        {transcripts[f.name].id ? (
                          <div style={{ marginTop: 6 }}>
                            <a href={`${API_BASE}/api/media/transcripts/${encodeURIComponent(String(transcripts[f.name].id))}`} target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>Open transcript JSON</a>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </>
              );
            })}
            {total === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: 0, border: 'none' }}>
                  <EmptyState
                    icon="📁"
                    title="No uploads yet"
                    description="Upload audio or video files to see them here. You can then transcribe, amplify, or create clips from them."
                  />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
          <span style={{ color: '#9ca3af' }}>
            Showing {total === 0 ? 0 : start + 1}-{Math.min(end, total)} of {total}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="subtle" disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
            <span style={{ color: '#9ca3af' }}>Page {safePage}/{maxPage}</span>
            <Button variant="subtle" disabled={safePage >= maxPage} onClick={() => setPage(p => Math.min(maxPage, p + 1))}>Next</Button>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
